import { useEffect, useRef, useState } from "react";

const DIRECTION_CHANGE_THRESHOLD = 8;

export interface ScrollState {
  direction: "up" | "down";
  atTop: boolean;
}

/**
 * Tracks the current vertical scroll direction, ignoring tiny jitters below
 * DIRECTION_CHANGE_THRESHOLD so hide/show UI (header, bottom nav) doesn't flicker.
 *
 * Pass `enabled={false}` to skip registering the scroll listener entirely
 * (e.g. on breakpoints where nothing reacts to scroll direction).
 */
export const useScrollDirection = (enabled = true): ScrollState => {
  const [state, setState] = useState<ScrollState>({
    direction: "up",
    atTop: typeof window === "undefined" ? true : window.scrollY <= 0,
  });
  const lastScrollY = useRef(
    typeof window === "undefined" ? 0 : window.scrollY,
  );

  useEffect(() => {
    if (typeof window === "undefined" || !enabled) {
      return;
    }

    let ticking = false;

    const updateDirection = (): void => {
      const scrollY = window.scrollY;
      const diff = scrollY - lastScrollY.current;

      if (Math.abs(diff) >= DIRECTION_CHANGE_THRESHOLD) {
        setState({
          direction: diff > 0 ? "down" : "up",
          atTop: scrollY <= 0,
        });
        lastScrollY.current = scrollY;
      } else if (scrollY <= 0) {
        setState((prev) =>
          prev.atTop ? prev : { direction: prev.direction, atTop: true },
        );
        lastScrollY.current = scrollY;
      }

      ticking = false;
    };

    const onScroll = (): void => {
      if (!ticking) {
        window.requestAnimationFrame(updateDirection);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return (): void => window.removeEventListener("scroll", onScroll);
  }, [enabled]);

  return state;
};
